import React, { useEffect, useRef, useState } from 'react';
import { useBodyScrollLock } from '../../../../hooks/useBodyScrollLock';
import { Col, Row } from 'reactstrap';
import './../../KnowledgeBaseContentManager/KnowledgeBaseContent.scss';
import './KnowledgeBaseOfferContentModal.scss';
import {
    Label,
    FormGroup,
    Button,
    ModalHeader,
    ModalBody,
    ModalFooter,
    InputGroup,
    InputGroupText,
} from 'reactstrap';
import {
    KNOWLEDGE_BASE_TYPES,
    KNOWLEDGE_BASE_TYPES_ENUM,
} from '../../../../utils/knowledgeBaseConstants';
import createNotification from '../../../../utils/createNotification';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { Field, Form, Input } from '@availity/form';
import * as yup from 'yup';
import Rodal from '../../../../components/Rodal/Rodal';
import Helpers from '../../../../utils/helper';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import { NOT_EMPTY_OR_WHITESPACE_REGEX } from '../../../../utils/constants';
import { useStateSelector } from '../../../../store/selectors';

interface Props {
    isVisible: boolean;
    setIsVisible: (value: boolean) => void;
}

interface OfferContentModel {
    title: string;
    introText: string;
    author: string;
    type: number;
    fileName?: string;
}

const KnowledgeBaseOfferContentModal: React.FC<Props> = (props) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [isSaving, setIsSaving] = useState(false);
    const [file, setFile] = useState<File | null>(null);

    const buildEmptyContent = (): OfferContentModel => ({
        title: '',
        introText: '',
        author: '',
        type: KNOWLEDGE_BASE_TYPES_ENUM.Other,
    });

    const [content, setContent] =
        useState<OfferContentModel>(buildEmptyContent);
    const modalForm = useRef<any>({});

    useBodyScrollLock(props.isVisible);

    const resetModal = () => {
        setFile(null);
        setIsSaving(false);
        props.setIsVisible(false);
    };

    useEffect(() => {
        const raw = localStorage.getItem('loggedInUserSummary');
        const initial = buildEmptyContent();
        if (raw) {
            const parsed = JSON.parse(raw);
            initial.author = parsed.name ?? '';
        }
        setContent(initial);
    }, []);

    const saveContent = () => {
        setIsSaving(true);
        const formData = new FormData();
        if (file) formData.append('File', file);
        formData.append('Data', JSON.stringify(content));
        axios
            .post('/api/KnowledgeBase/offer', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            .then((response: any) => {
                if (
                    Helpers.isResponseSuccessful(
                        response,
                        'An error occured while sending offered Knowledge Library Content.'
                    )
                ) {
                    createNotification(
                        'Knowledge Library Content has been successfully offered!',
                        'success'
                    );
                    resetModal();
                }
            })
            .catch((error: Error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const initSubmit = () => {
        modalForm.current.handleSubmit?.();
    };

    const changeFileHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files?.[0] ?? null;
        setFile(selected);

        const fileName = selected?.name ?? '';
        modalForm.current.setFieldValue?.('sourceFile', fileName, true);

        const extension = fileName.split('.').pop();
        const typeId = extension
            ? (KNOWLEDGE_BASE_TYPES.find((e) =>
                  e.extensions?.includes('.' + extension)
              )?.id ?? KNOWLEDGE_BASE_TYPES_ENUM.Other)
            : KNOWLEDGE_BASE_TYPES_ENUM.Other;

        modalForm.current.setFieldValue?.('type', typeId, false);
        setContent((prev) => ({ ...prev, fileName, type: typeId }));
    };

    return content ? (
        <div className="knowledge-base-content offer-content-popup">
            <Rodal
                visible={props.isVisible}
                onClose={() => resetModal()}
                animation={'fade'}
                showMask={true}
                className="modal-style kb-modal-content announcement-modal">
                <div className="body_white_box associate_member">
                    <ModalHeader>Offer Content</ModalHeader>
                    <ModalBody>
                        <Form
                            innerRef={(c: any) => {
                                modalForm.current = c;
                            }}
                            onSubmit={() => {
                                saveContent();
                            }}
                            initialValues={{
                                title: content.title,
                                author: content.author,
                                introText: content.introText,
                                sourceFile: content.fileName ?? '',
                            }}
                            validationSchema={yup.object().shape({
                                title: yup
                                    .string()
                                    .required()
                                    .matches(
                                        NOT_EMPTY_OR_WHITESPACE_REGEX,
                                        ' '
                                    ),
                                author: yup
                                    .string()
                                    .required()
                                    .matches(
                                        NOT_EMPTY_OR_WHITESPACE_REGEX,
                                        ' '
                                    ),
                                introText: yup
                                    .string()
                                    .required()
                                    .matches(
                                        NOT_EMPTY_OR_WHITESPACE_REGEX,
                                        ' '
                                    ),
                            })}>
                            <div
                                className="associate_form announcement_form"
                                style={{ borderBottom: 0 }}>
                                <p>
                                    Offer here any high quality content you have
                                    for the Knowledge Library. If VetValue
                                    accepts and publishes your proposed content,
                                    VetValue reserves the right to provide you
                                    with a modest honorarium denominated in VV
                                    Reward Credits which will be deposited into
                                    your wallet.
                                </p>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Field
                                                type="text"
                                                label="Title"
                                                name="title"
                                                maxLength={200}
                                                onChange={(
                                                    event: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    setContent((prev) => ({
                                                        ...prev,
                                                        title: event.target
                                                            .value,
                                                    }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Field
                                                type="text"
                                                label="Author"
                                                name="author"
                                                maxLength={200}
                                                onChange={(
                                                    event: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    setContent((prev) => ({
                                                        ...prev,
                                                        author: event.target
                                                            .value,
                                                    }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="introText">
                                                Intro Text
                                            </Label>
                                            <InfoTooltip text="You may want to include in your introduction,<br/> a brief description of your background and professional qualifications,<br/> for context." />
                                            <Field
                                                type="textarea"
                                                name="introText"
                                                onChange={(
                                                    event: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    setContent((prev) => ({
                                                        ...prev,
                                                        introText:
                                                            event.target.value,
                                                    }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="sourceFile">
                                                Source File
                                            </Label>
                                            <Label
                                                for="upload-file"
                                                className="upload-now">
                                                Upload New
                                            </Label>
                                            <InputGroup>
                                                <InputGroupText className="upload-file-group prepend-addon">
                                                    <label
                                                        htmlFor="upload-file"
                                                        className="choose-file-label">
                                                        Choose File
                                                    </label>
                                                    <input
                                                        type="file"
                                                        name="file"
                                                        id="upload-file"
                                                        className="upload-file-input"
                                                        onChange={
                                                            changeFileHandler
                                                        }
                                                    />
                                                </InputGroupText>
                                                <Input
                                                    type="text"
                                                    name="sourceFile"
                                                    disabled={true}
                                                    style={{
                                                        backgroundColor: '#fff',
                                                    }}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <div className="footer-wrapper-container">
                            <Button
                                className="cancel"
                                onClick={() => resetModal()}>
                                Cancel
                            </Button>
                            <ButtonLoader
                                buttonText={'Offer Content'}
                                loaderButtonText={''}
                                disabled={false}
                                isLoading={isSaving}
                                onClick={initSubmit}
                                className="btn btn-primary loader-submit"
                            />
                        </div>
                    </ModalFooter>
                </div>
            </Rodal>
        </div>
    ) : null;
};

export default KnowledgeBaseOfferContentModal;
